<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { ChapterConfig } from '../configs/types'
import { getChapterConfig } from '../configs/brands'
import { usePurchase } from '../composables/usePurchase'
import { useSubscription } from '../composables/useSubscription'
import { createShowPaymentMethodSelector } from '../composables/usePaymentMethodDisplay'
import { useBottomSwiper } from '../composables/useBottomSwiper'
import SubscribeItemContent from './SubscribeItemContent.novelarc.vue'
import PurchaseItemContent from './PurchaseItemContent.novelarc.vue'
import PaymentMethodSelector from './PaymentMethodSelector.vue'
import { useFbqPaymentPopupAddToCart } from '@/composables/fbq/useFbqPaymentPopupAddToCart'
import { useUserStore } from '@/stores'
import { VIP_TYPE } from '@/constants'
import 'swiper/css'

const props = defineProps<{
  contentId: string
  contentChapterId: string
  bookName: string
  chapterName: string
  serialStatus: number
  dark?: boolean
  markRecommendOnPay?: boolean
}>()

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'update:membership-only', val: boolean): void
}>()

const config: ChapterConfig = getChapterConfig()

const userStore = useUserStore()

const {
  templateProducts,
  PayTemplateSuccess,
  PayTemplate,
  currentPaymentMethod: purchasePaymentMethod,
  availablePaymentMethods: purchaseAvailableMethods,
  selectPaymentMethod: selectPurchasePaymentMethod,
  productLists: purchaseProductLists,
} = usePurchase('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
  bookName: props.bookName,
  chapterName: props.chapterName,
  serialStatus: props.serialStatus,
}, {
  markRecommendOnPay: props.markRecommendOnPay,
})

const {
  loaded,
  subscribeSuccess,
  getUnitPrice,
  onSubscribeProducts,
  isZero,
  availablePaymentMethods: subscriptAvailableMethods,
  selectPaymentMethod: selectSubscriptPaymentMethod,
  currentPaymentMethod: subscriptPaymentMethod,
  hasPayPalPasswordlessPayment,
  productLists: subscribeProductLists,
} = useSubscription('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
}, {
  markRecommendOnPay: props.markRecommendOnPay,
})

const active = ref(0)

const currentPaymentMethod = computed({
  get: () => {
    if (!purchaseProductLists.value || purchaseProductLists.value.length === 0)
      return subscriptPaymentMethod.value
    return purchasePaymentMethod.value
  },
  set: (val) => {
    selectPurchasePaymentMethod(val)
    selectSubscriptPaymentMethod(val)
  },
})

const availablePaymentMethods = computed(() => {
  if (hasPayPalPasswordlessPayment.value) {
    const all = [...(purchaseAvailableMethods.value || []), ...(subscriptAvailableMethods.value || [])]
    const mergedUnique = all.filter((m, i, arr) =>
      arr.findIndex(x => String(x.paymentMethod).toLowerCase() === String(m.paymentMethod).toLowerCase()) === i,
    )
    return mergedUnique.filter(m => String(m.paymentMethod).toLowerCase() === 'paypal')
  }

  const purchaseMethods = purchaseAvailableMethods.value || []
  const subscriptMethods = subscriptAvailableMethods.value || []

  if (purchaseMethods.length > subscriptMethods.length)
    return purchaseMethods
  if (subscriptMethods.length > purchaseMethods.length)
    return subscriptMethods

  if (purchaseMethods.length === 1 && subscriptMethods.length === 1) {
    const p = String(purchaseMethods[0]?.paymentMethod ?? '')
    const s = String(subscriptMethods[0]?.paymentMethod ?? '')
    if (p === s)
      return purchaseMethods
    const merged = [...purchaseMethods, ...subscriptMethods].filter(
      (m, i, arr) => arr.findIndex(x => String(x.paymentMethod) === String(m.paymentMethod)) === i,
    )
    return merged
  }

  return purchaseMethods
})

const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

const subscribes = computed(() => {
  const currentMethod = Number(currentPaymentMethod.value)
  if (!subscriptAvailableMethods.value || !Array.isArray(subscriptAvailableMethods.value))
    return []
  const currentMethodList = subscriptAvailableMethods.value.find(
    (list: any) => Number(list?.paymentMethod) === currentMethod,
  )
  if (!currentMethodList?.products || !Array.isArray(currentMethodList.products))
    return []
  return currentMethodList.products.filter((item: any) => item && item.purchasePopShow)
})

const isPurchaseFirst = computed(() => {
  const purchaseCount = templateProducts.value?.length ?? 0
  const subscribeCount = subscribes.value?.length ?? 0
  return purchaseCount > subscribeCount
})

// 无充值档位 + 订阅档位全量为「畅读会员」(VIP_TYPE.ALL)
const isMembershipOnly = computed(() => {
  if (!loaded.value)
    return false
  const noRecharge = !templateProducts.value || templateProducts.value.length === 0
  const subs = subscribes.value || []
  return noRecharge && subs.length > 0 && subs.every((item: any) => Number(item?.subscribeType) === VIP_TYPE.ALL)
})

watch(isMembershipOnly, val => emit('update:membership-only', val), { immediate: true })

const subscribeSwiperRef = ref<HTMLElement>()
const {
  slideIndex: subscribeSlideIndex,
  init: initSubscribeSwiper,
  destroy: destroySubscribeSwiper,
  onBulletClick: onSubscribeBulletClick,
} = useBottomSwiper(subscribeSwiperRef, () => subscribes.value)

const purchaseSwiperRef = ref<HTMLElement>()
const {
  slideIndex: purchaseSlideIndex,
  init: initPurchaseSwiper,
  destroy: destroyPurchaseSwiper,
  onBulletClick: onPurchaseBulletClick,
} = useBottomSwiper(purchaseSwiperRef, () => templateProducts.value ?? [])

function handlePayTemplate(item: any, index: number) {
  active.value = index
  PayTemplate(item)
}

function handleSubscribeProducts(item: any) {
  selectSubscriptPaymentMethod(currentPaymentMethod.value)
  onSubscribeProducts(item)
}

function shouldInitSubscribeSwiper() {
  return loaded.value && isPurchaseFirst.value && !userStore.$state.subscribe && (subscribes.value?.length ?? 0) > 0
}

function shouldInitPurchaseSwiper() {
  return loaded.value && !isPurchaseFirst.value && (templateProducts.value?.length ?? 0) > 0
}

watch(
  () => loaded.value,
  (val) => {
    if (!val)
      return
    if (shouldInitSubscribeSwiper())
      initSubscribeSwiper()
    if (shouldInitPurchaseSwiper())
      initPurchaseSwiper()
  },
  { immediate: true },
)

watch(
  () => templateProducts.value,
  (newVal) => {
    if (loaded.value && newVal?.length)
      initPurchaseSwiper()
  },
)

watch(
  () => subscribes.value,
  (newVal) => {
    if (isPurchaseFirst.value && loaded.value && newVal?.length)
      initSubscribeSwiper()
  },
)

watch(
  currentPaymentMethod,
  () => {
    if (shouldInitSubscribeSwiper())
      initSubscribeSwiper()
    else
      destroySubscribeSwiper()

    if (shouldInitPurchaseSwiper())
      initPurchaseSwiper()
    else
      destroyPurchaseSwiper()
  },
)

function handleVisibilityChange() {
  if (document.hidden)
    return
  if (shouldInitSubscribeSwiper())
    initSubscribeSwiper()
  if (shouldInitPurchaseSwiper())
    initPurchaseSwiper()
}

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  destroySubscribeSwiper()
  destroyPurchaseSwiper()
})

watch(
  () => subscribeSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)

watch(
  () => PayTemplateSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)

const { reportAll: reportFbqAddToCartAll } = useFbqPaymentPopupAddToCart()
watch(
  loaded,
  (isLoaded) => {
    if (isLoaded)
      reportFbqAddToCartAll(purchaseProductLists.value, subscribeProductLists.value)
  },
  { immediate: true },
)
</script>

<template>
  <div class="inline-panel-recharge-novelarc w-full relative" :class="{ 'is-dark': dark }">
    <!-- 充值在上（平铺） + 订阅在下（轮播） -->
    <div v-if="isPurchaseFirst" class="payment-content-wrapper" :class="{ 'mt-16px': !showPaymentMethodSelector }">
      <div v-if="templateProducts?.length" class="purchase-list-container">
        <div class="purchase-list">
          <div
            v-for="(item, index) in templateProducts"
            :key="index"
            class="purchase-list-item rounded-8px mb-3"
            @click="handlePayTemplate(item, index)"
          >
            <PurchaseItemContent :item="item" :is-active="active === index" />
          </div>
        </div>
      </div>

      <div v-if="!userStore.$state.subscribe && subscribes?.length" ref="subscribeSwiperRef" class="bottom-swiper subscribe-swiper-mode swiper mt-1">
        <div class="swiper-wrapper">
          <div
            v-for="(item, index) in subscribes"
            :key="index"
            class="swiper-slide bottom-swiper-item"
            @click="handleSubscribeProducts(item)"
          >
            <SubscribeItemContent :item="item" :get-unit-price="getUnitPrice" :is-zero="isZero" />
          </div>
        </div>
        <div v-if="subscribes.length > 1" class="bottom-swiper-pagination">
          <span
            v-for="(_, i) in subscribes"
            :key="i"
            class="swiper-pagination-bullet"
            :class="{ 'swiper-pagination-bullet-active': i === subscribeSlideIndex }"
            @click="onSubscribeBulletClick(i)"
          />
        </div>
      </div>
    </div>

    <!-- 订阅在上（平铺） + 充值在下（轮播） -->
    <div v-else class="payment-content-wrapper" :class="{ 'mt-16px': !showPaymentMethodSelector }">
      <div v-if="!userStore.$state.subscribe && subscribes?.length" class="subscribe-list-container">
        <div class="subscribe-list">
          <div
            v-for="(item, index) in subscribes"
            :key="index"
            class="subscribe-list-item rounded-8px mb-3"
            @click="handleSubscribeProducts(item)"
          >
            <SubscribeItemContent :item="item" :get-unit-price="getUnitPrice" :is-zero="isZero" />
          </div>
        </div>
      </div>

      <div v-if="templateProducts?.length" ref="purchaseSwiperRef" class="bottom-swiper purchase-swiper-bg swiper mt-1">
        <div class="swiper-wrapper">
          <div
            v-for="(item, index) in templateProducts"
            :key="index"
            class="swiper-slide bottom-swiper-item"
            @click="handlePayTemplate(item, index)"
          >
            <PurchaseItemContent :item="item" :is-active="active === index" />
          </div>
        </div>
        <div v-if="templateProducts.length > 1" class="bottom-swiper-pagination">
          <span
            v-for="(_, i) in templateProducts"
            :key="i"
            class="swiper-pagination-bullet"
            :class="{ 'swiper-pagination-bullet-active': i === purchaseSlideIndex }"
            @click="onPurchaseBulletClick(i)"
          />
        </div>
      </div>
    </div>

    <!-- 支付方式选择器（底部，large 变体） -->
    <template v-if="showPaymentMethodSelector">
      <PaymentMethodSelector
        variant="large"
        :transparent="true"
        :dark="dark"
        :model-value="currentPaymentMethod"
        :available-methods="availablePaymentMethods"
        @update:model-value="val => currentPaymentMethod = val"
      />
    </template>

    <div v-if="!loaded" class="h-160px flex items-center justify-center flex-col">
      <BasicSpinner class="mx-auto" />
      <div class="mt-2 font-bold">
        {{ config.texts.loading }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.inline-panel-recharge-novelarc {
  display: flex;
  flex-direction: column;
}

.payment-content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.subscribe-list-container {
  position: relative;
}

.subscribe-list {
  padding-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
}

.subscribe-list-item {
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }

  &:last-child {
    margin-bottom: 0 !important;
  }
}

.purchase-list-container {
  position: relative;
}

.purchase-list {
  padding-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
}

.purchase-list-item {
  background: #FFF4EC;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }

  &:last-child {
    margin-bottom: 0 !important;
  }
}

.purchase-swiper-bg {
  background: #FFF4EC;
}

.bottom-swiper {
  flex-shrink: 0;
  width: 100%;
  overflow: hidden;
  position: relative;
  border-radius: 8px;
  height: 91px;

  .bottom-swiper-item {
    width: 100%;
    height: 91px;
    cursor: pointer;
  }

  .bottom-swiper-pagination {
    position: absolute;
    bottom: 10px;
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.subscribe-swiper-mode {
    height: 100px;

    .bottom-swiper-item {
      height: 100px;
    }

    .bottom-swiper-item :deep(.subscribe-item-novelarc) {
      width: 100%;
      height: 100%;
      min-height: unset;
      box-sizing: border-box;
    }
  }

  // 充值轮播：卡片填满 slide，避免固定 91PX 与 rem 缩放的 slide 高度不一致导致角标未贴顶
  &.purchase-swiper-bg {
    .bottom-swiper-item :deep(.purchase-item-novelarc) {
      width: 100%;
      height: 100%;
      box-sizing: border-box;
    }
  }
}
</style>

<style lang="scss">
.inline-panel-recharge-novelarc {
  .bottom-swiper-pagination {
    .swiper-pagination-bullet {
      display: inline-block;
      width: 6px;
      height: 6px;
      margin: 0 4px;
      background: #D9D9D9;
      border-radius: 3px;
      opacity: 1;
      cursor: pointer;
    }

    .swiper-pagination-bullet-active {
      width: 20px;
      height: 6px;
      background: linear-gradient(90deg, #F7A82A 0%, #FE7E01 100%);
      border-radius: 3px;
      opacity: 1;
    }
  }
}
</style>
